import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';

import { getM1Overzicht, type M1FlowRegel } from '../m1.api';

/**
 * h6-5 (M1) — het interne overzicht van "in één keer goed".
 *
 * Bewust een tabel en geen dashboard: dit cijfer bestaat om één besluit te voeden — kan de
 * communicatie per stroom om. Wat het moet laten zien is dus smal, en wat het níet laat zien telt
 * net zo zwaar: geen klantnummers, want per klant zou een handvol pogingen al een percentage
 * opleveren dat niets betekent.
 */
// Beide grenzen tellen mee in de telling, dus min negenentwintig geeft dertig dagen.
const DAGEN_TERUG = 29;

function dag(verschuiving: number): string {
  const moment = new Date(Date.now() + verschuiving * 24 * 60 * 60 * 1000);
  return moment.toISOString().slice(0, 10);
}

export function M1Page() {
  const { t } = useTranslation();
  const [vanaf, setVanaf] = useState(() => dag(-DAGEN_TERUG));
  const [tot, setTot] = useState(() => dag(0));

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'm1', vanaf, tot],
    queryFn: () => getM1Overzicht(vanaf, tot),
  });

  const regels = useMemo<M1FlowRegel[]>(() => data?.perFlow ?? [], [data]);

  /**
   * Geen percentage bij een lege noemer. "0%" zou lezen als "niemand deed het goed" terwijl er
   * simpelweg niets gemeten is.
   */
  const percentage = (ratio: number | null) =>
    ratio == null ? t('admin.m1.geenGegevens') : `${Math.round(ratio * 100)}%`;

  /**
   * h7-3: hier is "geen gegevens" niet scherp genoeg. Een leeg cijfer kan ook betekenen dat de
   * periode verder terugkijkt dan de bewaartermijn, of dat er te weinig sessies zijn om iets over
   * een stroom te zeggen in plaats van over één klant.
   */
  const correctieWaarde = (waarde: number | null) =>
    waarde == null ? t('admin.m1.teWeinigGegevens') : String(waarde);

  const correctiePercentage = (ratio: number | null) =>
    ratio == null ? t('admin.m1.teWeinigGegevens') : `${Math.round(ratio * 100)}%`;

  /** In minuten, want in seconden leest niemand of tien minuten veel of weinig is. */
  const minuten = (seconden: number | null) =>
    seconden == null ? t('admin.m1.geenGegevens') : t('admin.m1.minuten', { aantal: Math.round(seconden / 60) });

  return (
    <div className="flex flex-col gap-4 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">{t('admin.m1.titel')}</h1>
        <p className="text-[var(--foreground-muted)]">{t('admin.m1.uitleg')}</p>
      </header>

      {/* Naast elkaar passen de twee datumvelden niet op een telefoon met een grote letter: het
          tweede viel bij 375px buiten beeld (gemeten 13-09-2026). Wrappen zet ze dan onder elkaar. */}
      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col text-sm">
          {t('admin.m1.vanaf')}
          <input type="date" value={vanaf} onChange={(event) => setVanaf(event.target.value)} className="rounded-[var(--radius-control)] border border-[var(--border)] px-2 py-1" />
        </label>
        <label className="flex flex-col text-sm">
          {t('admin.m1.tot')}
          <input type="date" value={tot} onChange={(event) => setTot(event.target.value)} className="rounded-[var(--radius-control)] border border-[var(--border)] px-2 py-1" />
        </label>
      </div>

      {isLoading && <p className="text-[var(--foreground-muted)]">{t('admin.m1.laden')}</p>}
      {isError && <p role="alert">{t('admin.m1.fout')}</p>}

      {data && (
        <Card>
          <CardContent>
            {/* Vijf of zes kolommen passen niet op een telefoon, en een tabel die uit haar kaart
                loopt neemt de hele pagina mee: bij 375px schoof het document 721px breed (gemeten
                13-09-2026). De eigen schuifbalk houdt dat binnen de tabel. */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.m1.stroom')}</TableHead>
                    <TableHead>{t('admin.m1.pogingen')}</TableHead>
                    <TableHead>{t('admin.m1.inEenKeerGoed')}</TableHead>
                    <TableHead>{t('admin.m1.storingen')}</TableHead>
                    <TableHead>{t('admin.m1.opgegeven')}</TableHead>
                    <TableHead>{t('admin.m1.percentage')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {regels.map((regel) => (
                    <TableRow key={regel.flow}>
                      <TableCell>{t(`intake.list.flow.${regel.flow}`)}</TableCell>
                      <TableCell>{regel.attempts}</TableCell>
                      <TableCell>{regel.firstTimeRight}</TableCell>
                      <TableCell>{regel.chainFailures}</TableCell>
                      <TableCell>{regel.abandoned}</TableCell>
                      <TableCell>{percentage(regel.ratio)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>{t('admin.m1.totaal')}</TableCell>
                    <TableCell>{data.totaal.attempts}</TableCell>
                    <TableCell>{data.totaal.firstTimeRight}</TableCell>
                    <TableCell>{data.totaal.chainFailures}</TableCell>
                    <TableCell>{data.totaal.abandoned}</TableCell>
                    <TableCell>{percentage(data.totaal.ratio)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            {/* De kanttekening staat bij het cijfer en niet in een voetnoot elders: wie dit leest
                hoort meteen te weten waar het blind voor is. */}
            <p className="mt-3 text-sm text-[var(--foreground-muted)]">{t('admin.m1.kanttekening')}</p>
            {/* De dagindeling is UTC. Wie een grensuur naremt komt anders op een andere dag uit. */}
            <p className="mt-1 text-sm text-[var(--foreground-muted)]">{t('admin.m1.tijdzone')}</p>

            {/* h6-6 (T3): dezelfde kaart, want de twee cijfers beantwoorden samen één vraag —
                is het portaal beter én niet trager? Los gelezen leiden ze tot het verkeerde besluit. */}
            <h2 className="mt-6 text-sm font-medium">{t('admin.m1.doorlooptijdTitel')}</h2>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.m1.stroom')}</TableHead>
                    <TableHead>{t('admin.m1.gemeten')}</TableHead>
                    <TableHead>{t('admin.m1.mediaan')}</TableHead>
                    <TableHead>{t('admin.m1.p90')}</TableHead>
                    <TableHead>{t('admin.m1.blijvenLiggen')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.doorlooptijd.map((regel) => (
                    <TableRow key={`t3-${regel.flow}`}>
                      <TableCell>{t(`intake.list.flow.${regel.flow}`)}</TableCell>
                      <TableCell>{regel.gemeten}</TableCell>
                      <TableCell>{minuten(regel.medianeSeconden)}</TableCell>
                      <TableCell>{minuten(regel.p90Seconden)}</TableCell>
                      <TableCell>{regel.blijvenLiggen}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="mt-1 text-sm text-[var(--foreground-muted)]">{t('admin.m1.doorlooptijdKanttekening')}</p>

            {/* h7-3: het derde cijfer dat een omschakelbesluit voedt. Een eigen tabel en geen kolom
                in de twee hierboven, want die tonen alleen stromen met verkeer — en juist een
                stroom zonder verkeer hoort hier zichtbaar leeg te staan. */}
            <h2 className="mt-6 text-sm font-medium">{t('admin.m1.correctieTitel')}</h2>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.m1.stroom')}</TableHead>
                    <TableHead>{t('admin.m1.voorgesteld')}</TableHead>
                    <TableHead>{t('admin.m1.gecorrigeerd')}</TableHead>
                    <TableHead>{t('admin.m1.percentage')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.correctieRatio ?? []).map((regel) => (
                    <TableRow key={`correctie-${regel.flow}`}>
                      <TableCell>{t(`intake.list.flow.${regel.flow}`)}</TableCell>
                      <TableCell>{correctieWaarde(regel.voorgesteld)}</TableCell>
                      <TableCell>{correctieWaarde(regel.gecorrigeerd)}</TableCell>
                      <TableCell>{correctiePercentage(regel.ratio)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="mt-1 text-sm text-[var(--foreground-muted)]">{t('admin.m1.correctieKanttekening')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
